import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Text, Heading } from '../styles/components';

const FormWrapper = styled.div`
  width: 60vw;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.xl};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundAlt};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.brand};
    border-radius: 4px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.theme.colors.border};
    z-index: 0;
  }
`;

const Step = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: ${props => 
    props.$active ? props.theme.colors.brand : 
    props.$completed ? props.theme.colors.brandPressed : 
    props.theme.colors.background};
  border: 2px solid ${props => 
    props.$active ? props.theme.colors.brand : 
    props.$completed ? props.theme.colors.brandPressed : 
    props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const QuestionWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const QuestionTitle = styled(Heading)`
  font-size: ${props => props.theme.typography.sizes.h4};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ChoiceGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const ChoiceButton = styled(Button)`
  && {
    background: ${props => props.$selected ? props.theme.colors.brand : props.theme.colors.background};
    border: 1.5px solid ${props => props.$selected ? props.theme.colors.brand : props.theme.colors.border};
    opacity: 1;
    
    &:hover {
      background: ${props => props.$selected ? props.theme.colors.brandHover : props.theme.colors.backgroundAlt};
    }
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1.5px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.sm};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: ${props => props.theme.typography.sizes.body};
  margin-top: ${props => props.theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.brand};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.brandHover}40;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1.5px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.sm};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: ${props => props.theme.typography.sizes.body};
  margin-top: ${props => props.theme.spacing.md};
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.brand};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.brandHover}40;
  }
`;

const WhyWeAsk = styled(Text)`
  color: ${props => props.theme.colors.textLight};
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  font-style: italic;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: ${props => props.theme.borders.widths.thin} solid ${props => props.theme.colors.border};
`;

const BulletList = styled.ul`
  margin: 0 0 ${props => props.theme.spacing.xl} 0;
  font-size: ${props => props.theme.lists.bullet.fontSize};
  line-height: ${props => props.theme.lists.bullet.lineHeight};
  list-style-type: ${props => props.theme.lists.bullet.style.listStyleType};
  padding-left: ${props => props.theme.lists.bullet.style.paddingLeft};

  li {
    margin-bottom: ${props => props.theme.lists.bullet.spacing};
    color: ${props => props.theme.lists.bullet.bulletColor};
  }
`;

const CommitmentList = styled(BulletList)`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ErrorMessage = styled(Text)`
  color: #e53e3e;
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  margin-top: ${props => props.theme.spacing.sm};
`;

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validateStep = (step, formData) => {
  switch (step) {
    case 0: // Introduction
      return formData.email ? validateEmail(formData.email) : true;
    case 1: // Business Context
      return !!formData.businessType;
    case 2: // Current Approach
      return formData.currentTools.length > 0;
    case 3: // Growth & AI
      return formData.growthFocus.trim().length > 0 && !!formData.aiExperience;
    case 4: // Challenges
      return formData.biggestChallenge.trim().length > 0;
    case 5: // Commitment
      return !!formData.timeCommitment && !!formData.successStoryPermission;
    default:
      return true;
  }
};

const STORAGE_KEY = 'moogul_beta_application';

const loadSavedFormData = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Error loading saved form data:', error);
    return null;
  }
};

const saveFormData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving form data:', error);
  }
};

const BetaApplicationForm = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(() => {
    // Try to load saved data on initial render
    return loadSavedFormData() || {
      email: '',
      businessType: '',
      currentTools: [],
      growthFocus: '',
      aiExperience: '',
      biggestChallenge: '',
      timeCommitment: null,
      successStoryPermission: null
    };
  });

  // Save form data whenever it changes
  useEffect(() => {
    saveFormData(formData);
  }, [formData]);

  const steps = [
    {
      title: 'Introduction',
      content: (
        <FormSection>
          <QuestionTitle>
            Help shape the future of strategic growth
          </QuestionTitle>
          <Text size="large" $mb={props => props.theme.spacing.lg}>
            As a beta tester, you'll:
          </Text>
          <BulletList>
            <li>Get priority access to Moogul before public launch</li>
            <li>Shape product development with direct feedback</li>
            <li>Receive dedicated onboarding support</li>
            <li>Lock in special founding member pricing</li>
            <li>Be featured in our launch success stories (optional)</li>
          </BulletList>
          <Text $mb={props => props.theme.spacing.xl}>
            This application takes about 5 minutes. We keep it focused because we value your time, 
            just like Moogul helps you focus your strategy.
          </Text>
          
          <QuestionWrapper>
            <QuestionTitle>To get started, enter your email address below:</QuestionTitle>
            <WhyWeAsk>
              We'll use this to send your application result and schedule your onboarding if accepted.
            </WhyWeAsk>
            <TextInput
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setError('');
              }}
              placeholder="you@example.com"
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </QuestionWrapper>
        </FormSection>
      )
    },
    {
      title: 'Business Context',
      content: (
        <FormSection>
          <QuestionWrapper>
            <QuestionTitle>What type of business do you run?</QuestionTitle>
            <WhyWeAsk>
              This helps us understand your workflow needs and ensure we can provide the right level of support during beta testing.
            </WhyWeAsk>
            <ChoiceGroup>
              {['Solopreneur', 'Small Team (2-10)', 'Agency', 'Other'].map(type => (
                <ChoiceButton
                  key={type}
                  $selected={formData.businessType === type}
                  onClick={() => {
                    setFormData({ ...formData, businessType: type });
                    setError('');
                  }}
                >
                  {type}
                </ChoiceButton>
              ))}
            </ChoiceGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </QuestionWrapper>
        </FormSection>
      )
    },
    {
      title: 'Current Approach',
      content: (
        <FormSection>
          <QuestionWrapper>
            <QuestionTitle>
              How do you currently manage your business strategy and content creation?
            </QuestionTitle>
            <WhyWeAsk>
              Understanding your current tools helps us make your transition to Moogul smooth and ensure we're building the right integrations.
            </WhyWeAsk>
            <ChoiceGroup>
              {[
                'Documents/spreadsheets',
                'Note-taking apps',
                'Project management tools',
                'AI writing tools',
                'Other'
              ].map(tool => (
                <ChoiceButton
                  key={tool}
                  $selected={formData.currentTools.includes(tool)}
                  onClick={() => {
                    const tools = formData.currentTools.includes(tool)
                      ? formData.currentTools.filter(t => t !== tool)
                      : [...formData.currentTools, tool];
                    setFormData({ ...formData, currentTools: tools });
                    setError('');
                  }}
                >
                  {tool}
                </ChoiceButton>
              ))}
            </ChoiceGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </QuestionWrapper>
        </FormSection>
      )
    },
    {
      title: 'Growth & AI',
      content: (
        <FormSection>
          <QuestionWrapper>
            <QuestionTitle>
              What's the primary growth goal you're working towards in the next 6 months?
            </QuestionTitle>
            <WhyWeAsk>
              This helps us understand how Moogul can best support your specific growth objectives and measure success during the beta.
            </WhyWeAsk>
            <TextInput
              value={formData.growthFocus}
              onChange={(e) => {
                setFormData({ ...formData, growthFocus: e.target.value });
                setError('');
              }}
              placeholder="e.g., Launch new service line, Scale to 6 figures..."
            />
          </QuestionWrapper>

          <QuestionWrapper>
            <QuestionTitle>
              How are you currently using AI in your business?
            </QuestionTitle>
            <WhyWeAsk>
              This helps us tailor your onboarding experience and ensure you get the most value from Moogul's AI capabilities.
            </WhyWeAsk>
            <ChoiceGroup>
              {[
                'Just starting to explore AI tools',
                'Using basic AI writing tools',
                'Advanced AI user',
                'Not using AI yet'
              ].map(experience => (
                <ChoiceButton
                  key={experience}
                  $selected={formData.aiExperience === experience}
                  onClick={() => {
                    setFormData({ ...formData, aiExperience: experience });
                    setError('');
                  }}
                >
                  {experience}
                </ChoiceButton>
              ))}
            </ChoiceGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </QuestionWrapper>
        </FormSection>
      )
    },
    {
      title: 'Challenges',
      content: (
        <FormSection>
          <QuestionWrapper>
            <QuestionTitle>
              What's your biggest challenge in keeping your strategy active and your team aligned?
            </QuestionTitle>
            <WhyWeAsk>
              Your response helps us understand where Moogul can make the biggest impact and prioritize feature development.
            </WhyWeAsk>
            <TextArea
              value={formData.biggestChallenge}
              onChange={(e) => {
                setFormData({ ...formData, biggestChallenge: e.target.value });
                setError('');
              }}
              placeholder="Tell us about your specific challenges..."
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </QuestionWrapper>
        </FormSection>
      )
    },
    {
      title: 'Commitment',
      content: (
        <FormSection>
          <QuestionWrapper>
            <QuestionTitle>Can you commit to:</QuestionTitle>
            <WhyWeAsk>
              We're looking for partners who can help us build something amazing. Your commitment helps ensure we get the insights needed to make Moogul truly valuable.
            </WhyWeAsk>
            <CommitmentList>
              <li>Testing Moogul for at least 30 days</li>
              <li>Providing feedback twice during the beta</li>
              <li>Joining one group feedback session</li>
            </CommitmentList>
            <ChoiceGroup>
              {['Yes', 'No'].map(answer => (
                <ChoiceButton
                  key={answer}
                  $selected={formData.timeCommitment === answer}
                  onClick={() => {
                    setFormData({ ...formData, timeCommitment: answer });
                    setError('');
                  }}
                >
                  {answer}
                </ChoiceButton>
              ))}
            </ChoiceGroup>
          </QuestionWrapper>

          <QuestionWrapper>
            <QuestionTitle>
              Would you be open to sharing your success story if Moogul helps you achieve significant results?
            </QuestionTitle>
            <WhyWeAsk>
              This helps us showcase real results and build trust with future users. No pressure - it's completely optional!
            </WhyWeAsk>
            <ChoiceGroup>
              {['Yes', 'No'].map(answer => (
                <ChoiceButton
                  key={answer}
                  $selected={formData.successStoryPermission === answer}
                  onClick={() => {
                    setFormData({ ...formData, successStoryPermission: answer });
                    setError('');
                  }}
                >
                  {answer}
                </ChoiceButton>
              ))}
            </ChoiceGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </QuestionWrapper>
        </FormSection>
      )
    }
  ];

  const handleNext = () => {
    if (!validateStep(currentStep, formData)) {
      if (currentStep === 0 && !formData.email) {
        setError('Please enter your email address to continue');
      } else {
        setError('Please complete all fields before continuing');
      }
      return;
    }

    if (currentStep === steps.length - 1) {
      // Clear saved data on successful submission
      localStorage.removeItem(STORAGE_KEY);
      onComplete(formData);
    } else {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError('');
  };

  return (
    <FormWrapper>
      <StepIndicator>
        {steps.map((_, index) => (
          <Step
            key={index}
            $active={currentStep === index}
            $completed={currentStep > index}
          >
            {index + 1}
          </Step>
        ))}
      </StepIndicator>

      {steps[currentStep].content}

      <NavigationButtons>
        {currentStep > 0 && (
          <Button $secondary onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          style={{ marginLeft: 'auto' }}
        >
          {currentStep === steps.length - 1 ? 'Submit' : 'Next'}
        </Button>
      </NavigationButtons>
    </FormWrapper>
  );
};

export default BetaApplicationForm; 